import moment from 'moment';
import {
  columns,
  COMPLIANCE_STATES,
  SURPLUS_CASH_REQUIRED_EQUATION_PERCENTAGE,
} from './constants';
import { pathOr, without } from 'ramda';
import _ from 'lodash';
import messages from './messages';
import {
  AFFORDABLE_PROGRAMS,
  MANAGEMENT_FEE_LABELS,
} from '../../utils/affordable';
import rdModalMessages from './RDBudgetModal/messages';
import type { ComplianceStateResult } from './types';
import SurplusCashTooltip from './RDBudgetModal/SurplusCashTooltip';
import { formatCurrency } from '@fortress-technology-solutions/fortress-component-library/utils';
import { parseMonetaryValue } from 'utils/parsers';
import type {
  BudgetType,
  SectionConfiguration,
} from 'containers/RentForecast/RDBudgetModal/types';

export function calculateWithProposed(proposed, value, proposedType) {
  return proposedType === 'percent'
    ? value * _.round(proposed / 100 + 1, 6)
    : value + proposed;
}

export const getFormattedPercentage = (value, usePercentage) => {
  if (value == null || Number.isNaN(value)) value = 0;

  if (!usePercentage) {
    return `$${value}`;
  }

  let percent;
  if (usePercentage && value === 1) {
    return '0%';
  } else if (value > 1.01 && value <= 2) {
    percent = (value - 1) * 100;
  } else if (value > 0 && value <= 1) {
    percent = value * 100;
  } else {
    percent = value;
  }

  const finalValue = percent.toFixed(2).replace(/\.?0+$/, '');

  return `${finalValue}%`;
};

export const getNewFilteredColumns = ({
  isRDFloorPlan,
  isHUDFloorPlan,
  isLIHTCFloorPlan,
  isAffordableMixedProperty,
}) => {
  return Object.keys(columns).filter((column) => {
    if (column === 'basicRent' && !isRDFloorPlan) {
      return false;
    } else if (column === 'hud236BasicRent' && !isHUDFloorPlan) {
      return false;
    } else if (column === 'hud236MarketRent' && !isHUDFloorPlan) {
      return false;
    } else if (column === 'noteRent' && !isRDFloorPlan) {
      return false;
    } else if (column === 'nonOptionalCharge' && !isLIHTCFloorPlan) {
      return false;
    } else if (
      column === 'financialMarketRentAmount' &&
      !isAffordableMixedProperty
    ) {
      return false;
    } else if (column === 'HUDGrossRent' && !isHUDFloorPlan) {
      return false;
    }
    return true;
  });
};

export const mapAllowances = ({ allowancesArray, allowanceValues, intl }) => {
  return allowancesArray.map((allowance) => {
    const name = pathOr('', ['pha', 'name'], allowance);
    const fees = allowanceValues
      .filter(
        (allowance) =>
          name === pathOr('', ['allowances', 'pha', 'name'], allowance),
      )
      .reduce(
        (prev, val) => {
          const valType = pathOr('', ['allowances', 'pha', 'name'], val);
          val.uaTypes = without(
            [null, undefined],
            (val?.fpuaTypes ?? []).map(
              (fpuaType) => fpuaType?.utilityAllowanceType,
            ),
          );
          if (valType === name) prev.list.push(val);
          const today = moment().unix();
          const start = moment(val.startDate).startOf('day').unix();
          const end =
            val.endDate !== null
              ? moment(val.endDate).endOf('day').unix()
              : null;
          if (today > start && (end === null || today < end)) {
            prev.currentRate = val.feeAmount;
          }
          return prev;
        },
        { list: [], currentRate: allowance.utilityAllowanceAmount },
      );
    const uaMessage = `${name} ${intl.formatMessage(
      messages.utilityAllowance,
    )}`;

    return {
      name,
      uaMessage,
      ...fees,
    };
  });
};

export const hasMissingCommentsOnOtherFields = ({
  formValues,
  partIndex,
  items,
}) => {
  const fieldsToCheck =
    partIndex === 3
      ? ['reserveProposed', 'operatingProposed']
      : ['proposedBudget'];

  for (const item of items) {
    for (const row of item.rowIndexes) {
      const hasNonZero = fieldsToCheck.some((field) => {
        const key = `${partIndex}-${item.sectionIndex}-${row}-${field}`;

        return parseMonetaryValue(formValues[key]) !== 0;
      });

      const commentKey = `${partIndex}-${item.sectionIndex}-${row}-comments`;
      const comment = (formValues[commentKey] ?? '').trim();

      if (hasNonZero && !comment) {
        return true;
      }
    }
  }

  return false;
};

export const getAffordableFees = (fees: Object): Array<[string, number]> => {
  const programs = _.keys(AFFORDABLE_PROGRAMS);

  return _.entries(fees)
    .filter(([key]) => programs.some((program) => key.startsWith(program)))
    .map(([key, value]) => [key.replace('Fee', ''), value]);
};

export const getManagementFees = (
  managementFees: Object,
): Array<[string, number]> => {
  return _.toPairs(managementFees)
    .filter(([, applied]) => applied)
    .map(([key]) => [MANAGEMENT_FEE_LABELS[key], 5]);
};

export const calculateRTOComplianceState = (
  vacancyRate: number,
  partILine23ProposedValue: number,
  partILine23CurrentValue: number,
  intl: Object,
): ComplianceStateResult => {
  if (vacancyRate >= 10 && partILine23ProposedValue > 0) {
    return {
      state: COMPLIANCE_STATES.warning,
      tooltip: intl.formatMessage(
        rdModalMessages.complianceItemsPartI23ReturnToOwnerTooltipWarning1,
      ),
    };
  }

  if (
    vacancyRate < 10 &&
    partILine23ProposedValue !== partILine23CurrentValue
  ) {
    return {
      state: COMPLIANCE_STATES.warning,
      tooltip: intl.formatMessage(
        rdModalMessages.complianceItemsPartI23ReturnToOwnerTooltipWarning2,
      ),
    };
  }

  return { state: COMPLIANCE_STATES.pass, tooltip: null };
};

export const calculateSurplusComplianceState = (
  partILine33ProposedValue: number,
  partIILine41ProposedValue: number,
  partIILine40ProposedValue: number,
): ComplianceStateResult => {
  const cashAvailable = partILine33ProposedValue;
  const cashRequired =
    (partIILine41ProposedValue - partIILine40ProposedValue) *
      SURPLUS_CASH_REQUIRED_EQUATION_PERCENTAGE +
    partIILine40ProposedValue;
  const surplus = _.round(cashRequired - cashAvailable, 2);
  const normalizedSurplus = surplus === 0 ? 0 : surplus; // Round could return -0 due to very small negative result

  return {
    state:
      normalizedSurplus >= 0
        ? COMPLIANCE_STATES.pass
        : COMPLIANCE_STATES.warning,
    tooltip: <SurplusCashTooltip />,
    value: normalizedSurplus,
  };
};

export const calculateEndingBalanceComplianceState = (
  intl: Object,
  partIIILine8ProposedValue: number,
  partIIIReservedMaxAmount: number,
  partIIIInsuranceDeductibleAmount: number,
  unitCount: number,
  has538Loan: boolean,
): ComplianceStateResult => {
  const compliance: ComplianceStateResult = {
    state: COMPLIANCE_STATES.pass,
    tooltip: null,
  };

  if (partIIILine8ProposedValue < 0) {
    compliance.state = COMPLIANCE_STATES.warning;
    compliance.tooltip = intl.formatMessage(
      rdModalMessages.complianceItemsPartIIIEndingBalanceValidationNegative,
    );
  } else if (partIIILine8ProposedValue > partIIIReservedMaxAmount) {
    compliance.state = COMPLIANCE_STATES.warning;
    compliance.tooltip = intl.formatMessage(
      rdModalMessages.complianceItemsPartIIIEndingBalanceValidationReservedMaxAmount,
      {
        partIIIReservedMaxAmount: formatCurrency(partIIIReservedMaxAmount, {
          minimumFractionDigits: 2,
        }),
      },
    );
  } else if (partIIILine8ProposedValue < partIIIInsuranceDeductibleAmount) {
    compliance.state = COMPLIANCE_STATES.warning;
    compliance.tooltip = intl.formatMessage(
      rdModalMessages.complianceItemsPartIIIEndingBalanceValidationInsuranceDeductibleAmount,
      {
        partIIIInsuranceDeductibleAmount: formatCurrency(
          partIIIInsuranceDeductibleAmount,
          { minimumFractionDigits: 2 },
        ),
      },
    );
  } else if (has538Loan && partIIILine8ProposedValue < unitCount * 1000) {
    compliance.state = COMPLIANCE_STATES.warning;
    compliance.tooltip = intl.formatMessage(
      rdModalMessages.complianceItemsPartIIIEndingBalanceValidation538Loan,
      {
        minimumEndingBalance: formatCurrency(unitCount * 1000, {
          minimumFractionDigits: 2,
        }),
      },
    );
  }

  return compliance;
};

export const rentForecastExist = (
  name: string,
  year: string,
  rentForecastList: Array<Object>,
): boolean => {
  return rentForecastList.some(
    (rentForecast) =>
      rentForecast.name.toLowerCase().trim() + rentForecast.year ===
      name.toLowerCase().trim() + year,
  );
};

/**
 * Get the date string to use in RD budget headers.
 * If {dateRange} is not specified then default ranges be from beginning to half of year
 *
 * @param currentYear
 * @param dateRange
 * @returns {`01/01/${number} - 06/30/${number}`|string}
 */
export const resolveActualDateLabel = (
  currentYear: number,
  dateRange: ?{ from: string, to: string },
): string => {
  return [
    dateRange?.from
      ? moment(dateRange?.from).format('MM/DD/YYYY')
      : `01/01/${currentYear}`,
    dateRange?.to
      ? moment(dateRange?.to).format('MM/DD/YYYY')
      : `06/30/${currentYear}`,
  ].join(' - ');
};

export const resolveBudgetSections = (
  sections: SectionConfiguration[],
  budgetType: BudgetType,
  parameters = {},
) => {
  return sections.map((section) => {
    const rows = section.rows.map((row) => {
      const disabledKeys = Object.entries(row.disabledWhen ?? {}).reduce(
        (acc, [key, types]) => {
          if (types.includes(budgetType)) {
            acc.push(key);
          }

          return acc;
        },
        [],
      );
      const labelTooltip = row.dynamicTooltip
        ? row.dynamicTooltip(parameters)
        : row.labelTooltip;
      const forceEnableKeys = row.dynamicForceEnableKeys
        ? row.dynamicForceEnableKeys({ budgetType })
        : row.forceEnableKeys;

      if (row.dynamicTooltip) {
        row.labelTooltip = row.dynamicTooltip(parameters);
      }

      if (row.dynamicForceEnableKeys) {
        row.forceEnableKeys = row.dynamicForceEnableKeys({ budgetType });
      }

      return { ...row, disabledKeys, labelTooltip, forceEnableKeys };
    });

    return { ...section, rows };
  });
};

export const differencesToHighlight = (differences) => {
  const differentFields = Object.entries(differences ?? {})
    .filter(([, val]) => val)
    .map(([fieldName]) => fieldName);

  return [
    Object.fromEntries(
      differentFields.map((fieldName) => [fieldName, 'warning.lighter']),
    ),
    differentFields.length,
  ];
};

export const getQueryStringValue = (name: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);

  return urlParams.get(name) ?? null;
};

export const getForecastName = (rentForecast, forecastsWithApprovalNumbers) => {
  if (!rentForecast) {
    return '';
  }

  let name = rentForecast.name;

  if (rentForecast.approvedFrom && rentForecast.approvalNumber) {
    name +=
      ' - Approved' +
      (forecastsWithApprovalNumbers[rentForecast.approvedFrom]
        ? ` (${rentForecast.approvalNumber})`
        : '');
  }

  return name;
};
